import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api';
import { toast } from 'sonner';
import { useWalkthroughContext } from '@/components/walkthrough';
import { getTourConfig } from '@/components/walkthrough/tourConfigs';
import {
  Search, BookOpen, ChevronRight, ArrowLeft, ThumbsUp, ThumbsDown,
  Clock, Eye, Star, FileText, HelpCircle, AlertTriangle, CheckCircle,
  Lightbulb, Wrench, Scale, List, BarChart3, Shield, Package, ClipboardList,
  Users, Settings, Bell, Share2, FolderOpen, Layers, Loader2, Home,
  MessageSquare, Tag, Filter, TrendingUp, Zap, Info, Compass, Play
} from 'lucide-react';

const ICON_MAP = {
  BookOpen, FileText, HelpCircle, AlertTriangle, CheckCircle, Lightbulb,
  Wrench, Scale, List, BarChart3, Shield, Package, ClipboardList, Users,
  Settings, Bell, Share2, FolderOpen, Layers, Star, MessageSquare, Tag,
  Filter, TrendingUp, Zap, Info, Home, Search, Eye,
};

const TYPE_BADGES = {
  overview: { label: 'Overview', color: 'bg-blue-100 text-blue-700' },
  'how-to': { label: 'How-To', color: 'bg-green-100 text-green-700' },
  'field-reference': { label: 'Reference', color: 'bg-purple-100 text-purple-700' },
  workflow: { label: 'Workflow', color: 'bg-amber-100 text-amber-700' },
  troubleshooting: { label: 'Troubleshooting', color: 'bg-red-100 text-red-700' },
  faq: { label: 'FAQ', color: 'bg-cyan-100 text-cyan-700' },
  'best-practice': { label: 'Best Practice', color: 'bg-emerald-100 text-emerald-700' },
  'contract-specific': { label: 'Contract Guide', color: 'bg-indigo-100 text-indigo-700' },
};

function MarkdownContent({ content }) {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-base font-bold text-slate-900 mt-6 mb-2">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-lg font-bold text-slate-900 mt-8 mb-3 pb-2 border-b border-slate-200">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="text-xl font-bold text-slate-900 mt-6 mb-3">{line.slice(2)}</h1>);
    } else if (line.startsWith('> **')) {
      const text = line.slice(2);
      elements.push(
        <div key={i} className="bg-amber-50 border-l-4 border-amber-400 p-3 my-3 rounded-r text-sm text-amber-800">
          <span dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
        </div>
      );
    } else if (line.startsWith('> ')) {
      elements.push(
        <div key={i} className="bg-slate-50 border-l-4 border-slate-300 p-3 my-3 rounded-r text-sm text-slate-700">
          {line.slice(2)}
        </div>
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        const itemText = lines[i].slice(2);
        items.push(
          <li key={i} className="text-sm text-slate-700 leading-relaxed">
            <span dangerouslySetInnerHTML={{ __html: itemText.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>').replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded text-xs font-mono">$1</code>') }} />
          </li>
        );
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc list-inside space-y-1 my-2 pl-2">{items}</ul>);
      continue;
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const itemText = lines[i].replace(/^\d+\.\s/, '');
        items.push(
          <li key={i} className="text-sm text-slate-700 leading-relaxed">
            <span dangerouslySetInnerHTML={{ __html: itemText.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>').replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded text-xs font-mono">$1</code>') }} />
          </li>
        );
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-decimal list-inside space-y-1 my-2 pl-2">{items}</ol>);
      continue;
    } else if (line.startsWith('[Screenshot')) {
      elements.push(
        <div key={i} className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-6 my-4 text-center">
          <Eye className="h-8 w-8 text-slate-400 mx-auto mb-2" />
          <p className="text-xs text-slate-500 italic">{line.replace(/[\[\]]/g, '')}</p>
        </div>
      );
    } else if (line.startsWith('---')) {
      elements.push(<hr key={i} className="my-6 border-slate-200" />);
    } else if (line.startsWith('|') && line.endsWith('|')) {
      // Parse markdown tables
      const tableRows = [];
      while (i < lines.length && lines[i].startsWith('|') && lines[i].endsWith('|')) {
        tableRows.push(lines[i]);
        i++;
      }
      if (tableRows.length >= 2) {
        const headerCells = tableRows[0].split('|').filter(c => c.trim());
        const dataRows = tableRows.slice(2); // Skip header and separator
        elements.push(
          <div key={`table-${i}`} className="overflow-x-auto my-4">
            <table className="w-full text-xs border border-slate-200 rounded">
              <thead>
                <tr className="bg-slate-50">
                  {headerCells.map((c, ci) => <th key={ci} className="px-3 py-2 text-left font-semibold text-slate-700 border-b border-slate-200">{c.trim()}</th>)}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, ri) => {
                  const cells = row.split('|').filter(c => c.trim());
                  return (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      {cells.map((c, ci) => <td key={ci} className="px-3 py-2 text-slate-600 border-b border-slate-100">{c.trim()}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(
        <p key={i} className="text-sm text-slate-700 leading-relaxed my-1">
          <span dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>').replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded text-xs font-mono">$1</code>') }} />
        </p>
      );
    }
    i++;
  }
  return <div>{elements}</div>;
}


export default function KnowledgeBase() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const walkthroughCtx = useWalkthroughContext();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [guidedTours, setGuidedTours] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [feedbackText, setFeedbackText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [contractFilter, setContractFilter] = useState('all');
  const view = searchParams.get('view') || 'home';
  const articleId = searchParams.get('article');
  const catId = searchParams.get('category');

  useEffect(() => {
    loadCategories();
    loadFeatured();
    loadPopular();
    loadGuidedTours();
  }, []);

  useEffect(() => {
    if (articleId) {
      loadArticle(articleId);
    } else {
      setSelectedArticle(null);
    }
  }, [articleId]);

  useEffect(() => {
    if (catId) {
      loadCategoryArticles(catId);
    }
  }, [catId, typeFilter, contractFilter]);

  const loadCategories = async () => {
    try {
      const res = await api.get('/kb/categories');
      setCategories(res.data.categories || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadFeatured = async () => {
    try {
      const res = await api.get('/kb/articles?featured=true&limit=8');
      setFeaturedArticles(res.data.articles || []);
    } catch (e) { console.error(e); }
  };

  const loadPopular = async () => {
    try {
      const res = await api.get('/kb/articles/popular?limit=6');
      setPopularArticles(res.data.articles || []);
    } catch (e) { console.error(e); }
  };

  const loadGuidedTours = async () => {
    try {
      const res = await api.get('/kb/guided-tours?active=true');
      setGuidedTours(res.data.tours || []);
    } catch (e) { console.error(e); }
  };

  const launchTour = (tourModuleId) => {
    if (!walkthroughCtx) return;
    const config = getTourConfig(tourModuleId);
    if (config) {
      walkthroughCtx.resetTour(tourModuleId);
      // Navigate to the appropriate page first
      const pathMap = {
        delay_events: null, // These are on ProjectDetail
        notices: null,
        claims: null,
        rfis: null,
        site_instructions: null,
        drawings: null,
        shop_drawings: null,
        programs: null,
        meeting_minutes: null,
        variation_orders: null,
        irs: null,
        procurement: null,
        tasks: '/tasks',
        reports: '/reports',
        executive_dashboard: '/executive-dashboard',
        correspondence: null,
        project_setup: null,
      };
      const path = pathMap[tourModuleId];
      if (path) {
        navigate(path);
        setTimeout(() => walkthroughCtx.startTour(tourModuleId, config.steps), 600);
      } else {
        toast.info('Navigate to a project first, then the tour will guide you through the module.');
      }
    }
  };

  const loadCategoryArticles = async (cid) => {
    try {
      let url = `/kb/articles?category_id=${cid}&limit=100`;
      if (typeFilter !== 'all') url += `&article_type=${typeFilter}`;
      if (contractFilter !== 'all') url += `&contract=${contractFilter}`;
      const res = await api.get(url);
      setArticles(res.data.articles || []);
      const cat = categories.find(c => c.id === cid);
      setSelectedCategory(cat || null);
    } catch (e) { console.error(e); }
  };

  const loadArticle = async (id) => {
    try {
      const res = await api.get(`/kb/articles/${id}`);
      setSelectedArticle(res.data);
    } catch (e) { console.error(e); toast.error('Article not found'); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    try {
      let url = `/kb/articles/search?q=${encodeURIComponent(searchQuery)}`;
      if (contractFilter !== 'all') url += `&contract=${contractFilter}`;
      const res = await api.get(url);
      setSearchResults(res.data.results || []);
      setSearchParams({ view: 'search' });
    } catch (e) { console.error(e); }
  };

  const submitFeedback = async (articleId, helpful) => {
    try {
      await api.post(`/kb/articles/${articleId}/feedback`, { helpful, feedback_text: feedbackText });
      setFeedbackGiven(prev => ({ ...prev, [articleId]: helpful }));
      toast.success('Thank you for your feedback!');
    } catch (e) { console.error(e); }
  };

  const goHome = () => { setSearchParams({}); setSearchResults(null); setSearchQuery(''); setSelectedArticle(null); setSelectedCategory(null); };
  const goCategory = (cid) => setSearchParams({ view: 'category', category: cid });
  const goArticle = (aid) => setSearchParams({ view: 'article', article: aid });

  const getCategoryIcon = (iconName) => {
    const Icon = ICON_MAP[iconName] || BookOpen;
    return Icon;
  };

  // ── Article View ──
  if (selectedArticle && view === 'article') {
    const a = selectedArticle;
    const tb = TYPE_BADGES[a.article_type] || TYPE_BADGES.overview;
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-slate-50" data-testid="kb-article-view">
          <div className="max-w-4xl mx-auto px-4 py-6">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-4 flex-wrap">
              <button onClick={goHome} className="hover:text-slate-800 flex items-center gap-1"><Home className="h-3 w-3" /> Help Center</button>
              <ChevronRight className="h-3 w-3" />
              {a.category && <><button onClick={() => goCategory(a.category_id)} className="hover:text-slate-800">{a.category.title}</button><ChevronRight className="h-3 w-3" /></>}
              <span className="text-slate-700 font-medium truncate max-w-[200px]">{a.title}</span>
            </div>

            {/* Article Header */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 mb-4">
              <div className="flex items-start gap-3 mb-3 flex-wrap">
                <Badge className={`${tb.color} text-[10px]`}>{tb.label}</Badge>
                {a.contract_visibility?.filter(c => c !== 'all').map(c => <Badge key={c} variant="outline" className="text-[10px]">{c.toUpperCase()}</Badge>)}
                {a.module && <Badge variant="outline" className="text-[10px]">{a.module}</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{a.title}</h1>
              {a.summary && <p className="text-sm text-slate-600 mb-3">{a.summary}</p>}
              <div className="flex items-center gap-4 text-xs text-slate-400">
                {a.updated_at && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Updated {new Date(a.updated_at).toLocaleDateString()}</span>}
                {a.view_count > 0 && <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {a.view_count} views</span>}
                {a.version && <span>v{a.version}</span>}
              </div>
            </div>

            {/* Quick Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {a.who_uses && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-[10px] font-bold uppercase text-blue-600 mb-1">Who Uses This</p>
                  <p className="text-xs text-blue-800">{a.who_uses}</p>
                </div>
              )}
              {a.when_to_use && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-[10px] font-bold uppercase text-green-600 mb-1">When To Use</p>
                  <p className="text-xs text-green-800">{a.when_to_use}</p>
                </div>
              )}
              {a.preconditions && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-[10px] font-bold uppercase text-amber-600 mb-1">Prerequisites</p>
                  <p className="text-xs text-amber-800">{a.preconditions}</p>
                </div>
              )}
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 mb-4">
              <MarkdownContent content={a.content} />
            </div>

            {/* Common Mistakes */}
            {a.common_mistakes && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-4">
                <h3 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Common Mistakes</h3>
                <MarkdownContent content={a.common_mistakes} />
              </div>
            )}

            {/* Best Practices */}
            {a.best_practices && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 mb-4">
                <h3 className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-2"><Lightbulb className="h-4 w-4" /> Best Practices</h3>
                <MarkdownContent content={a.best_practices} />
              </div>
            )}

            {/* Outputs */}
            {a.outputs_next && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-4">
                <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><ChevronRight className="h-4 w-4" /> What Happens Next</h3>
                <MarkdownContent content={a.outputs_next} />
              </div>
            )}

            {/* Related Articles */}
            {a.related_articles_data?.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Related Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {a.related_articles_data.map(r => (
                    <button key={r.id} onClick={() => goArticle(r.id)} className="flex items-center gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50 text-left transition-colors">
                      <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-800 truncate">{r.title}</p>
                        {r.summary && <p className="text-[10px] text-slate-500 truncate">{r.summary}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Feedback */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Was this article helpful?</h3>
              {feedbackGiven[a.id] !== undefined ? (
                <p className="text-sm text-emerald-600 flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Thank you for your feedback!</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => submitFeedback(a.id, true)} className="gap-1"><ThumbsUp className="h-3.5 w-3.5" /> Yes</Button>
                    <Button size="sm" variant="outline" onClick={() => submitFeedback(a.id, false)} className="gap-1"><ThumbsDown className="h-3.5 w-3.5" /> No</Button>
                  </div>
                  <Textarea placeholder="Optional: Tell us how we can improve this article..." value={feedbackText} onChange={e => setFeedbackText(e.target.value)} className="text-xs h-16" />
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Category View ──
  if (catId && view === 'category') {
    const cat = categories.find(c => c.id === catId) || selectedCategory;
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-slate-50" data-testid="kb-category-view">
          <div className="max-w-5xl mx-auto px-4 py-6">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
              <button onClick={goHome} className="hover:text-slate-800 flex items-center gap-1"><Home className="h-3 w-3" /> Help Center</button>
              <ChevronRight className="h-3 w-3" />
              <span className="text-slate-700 font-medium">{cat?.title || 'Category'}</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{cat?.title}</h1>
            {cat?.description && <p className="text-sm text-slate-600 mb-4">{cat.description}</p>}

            <div className="flex gap-3 mb-4 flex-wrap">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Article Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(TYPE_BADGES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={contractFilter} onValueChange={setContractFilter}>
                <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="Contract" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Contracts</SelectItem>
                  <SelectItem value="jbcc">JBCC</SelectItem>
                  <SelectItem value="nec3">NEC3</SelectItem>
                  <SelectItem value="nec4">NEC4</SelectItem>
                  <SelectItem value="fidic">FIDIC 1999</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {articles.length === 0 ? (
              <div className="text-center py-12 text-slate-400"><BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No articles in this category yet.</p></div>
            ) : (
              <div className="space-y-2">
                {articles.map(a => {
                  const tb = TYPE_BADGES[a.article_type] || TYPE_BADGES.overview;
                  return (
                    <button key={a.id} onClick={() => goArticle(a.id)} className="w-full text-left bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all" data-testid={`article-${a.slug}`}>
                      <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-sm font-semibold text-slate-900">{a.title}</h3>
                            <Badge className={`${tb.color} text-[10px]`}>{tb.label}</Badge>
                            {a.featured && <Star className="h-3 w-3 text-amber-400 fill-amber-400" />}
                          </div>
                          {a.summary && <p className="text-xs text-slate-500 line-clamp-2">{a.summary}</p>}
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                            {a.module && <span>{a.module}</span>}
                            {a.view_count > 0 && <span>{a.view_count} views</span>}
                            {a.tags?.length > 0 && <span className="flex items-center gap-1"><Tag className="h-2.5 w-2.5" /> {a.tags.slice(0, 3).join(', ')}</span>}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Search Results ──
  if (view === 'search' && searchResults) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-slate-50" data-testid="kb-search-view">
          <div className="max-w-5xl mx-auto px-4 py-6">
            <button onClick={goHome} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-3 w-3" /> Back to Help Center</button>
            <div className="flex gap-2 mb-6">
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Search help articles..." className="h-9 text-sm" />
              <Button onClick={handleSearch} size="sm" className="gap-1"><Search className="h-3.5 w-3.5" /> Search</Button>
            </div>
            <p className="text-sm text-slate-500 mb-4">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"</p>
            {searchResults.length === 0 ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-2">No results found</p>
                <p className="text-xs text-slate-400">Try different keywords or browse categories</p>
              </div>
            ) : (
              <div className="space-y-2">
                {searchResults.map(a => {
                  const tb = TYPE_BADGES[a.article_type] || TYPE_BADGES.overview;
                  return (
                    <button key={a.id} onClick={() => goArticle(a.id)} className="w-full text-left bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-sm font-semibold text-slate-900">{a.title}</h3>
                        <Badge className={`${tb.color} text-[10px]`}>{tb.label}</Badge>
                      </div>
                      {a.summary && <p className="text-xs text-slate-500 line-clamp-2">{a.summary}</p>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Home View ──
  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-50" data-testid="kb-home">
        {/* Hero */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-white px-4 py-12 sm:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="h-10 w-10 mx-auto mb-4 text-cyan-400" />
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Help Center</h1>
            <p className="text-sm text-slate-400 mb-6">Find answers, learn workflows, and master the platform</p>
            <div className="flex gap-2 max-w-lg mx-auto">
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search for help articles, modules, workflows..."
                className="h-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="kb-search-input"
              />
              <Button onClick={handleSearch} className="bg-cyan-500 hover:bg-cyan-600 h-10 px-5" data-testid="kb-search-button">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Categories Grid */}
          {categories.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Browse by Category</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {categories.filter(c => !c.parent_id).map(cat => {
                  const Icon = getCategoryIcon(cat.icon);
                  const childCount = articles.length; // Will be fetched per category
                  return (
                    <button key={cat.id} onClick={() => goCategory(cat.id)} className="bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all text-left" data-testid={`category-${cat.id}`}>
                      <Icon className="h-6 w-6 text-slate-600 mb-2" />
                      <h3 className="text-sm font-semibold text-slate-900 mb-0.5">{cat.title}</h3>
                      {cat.description && <p className="text-[10px] text-slate-500 line-clamp-2">{cat.description}</p>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Featured Articles */}
          {featuredArticles.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Star className="h-5 w-5 text-amber-400" /> Featured Articles</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {featuredArticles.map(a => (
                  <button key={a.id} onClick={() => goArticle(a.id)} className="bg-white rounded-lg border border-slate-200 p-4 hover:border-amber-300 hover:shadow-sm transition-all text-left">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${(TYPE_BADGES[a.article_type] || TYPE_BADGES.overview).color} text-[10px]`}>{(TYPE_BADGES[a.article_type] || TYPE_BADGES.overview).label}</Badge>
                      <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">{a.title}</h3>
                    {a.summary && <p className="text-[10px] text-slate-500 line-clamp-2">{a.summary}</p>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Guided Tours Section */}
          <div className="mb-10" data-testid="kb-guided-tours-section">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Compass className="h-5 w-5 text-cyan-500" /> Interactive Guided Tours</h2>
            <p className="text-xs text-slate-500 mb-4">Step-by-step walkthroughs that highlight real UI elements and teach you each module.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { id: 'delay_events', name: 'Delay Events', desc: 'Learn to record and escalate delay events', icon: AlertTriangle, steps: 7 },
                { id: 'notices', name: 'Notices', desc: 'Issue and track contractual notices', icon: FileText, steps: 7 },
                { id: 'claims', name: 'Claims', desc: 'Build and submit EOT and cost claims', icon: Shield, steps: 7 },
                { id: 'rfis', name: 'RFIs', desc: 'Request and track information from consultants', icon: HelpCircle, steps: 7 },
                { id: 'site_instructions', name: 'Site Instructions', desc: 'Capture and respond to site directions', icon: ClipboardList, steps: 7 },
                { id: 'variation_orders', name: 'Variation Orders', desc: 'Manage scope changes and approvals', icon: FileText, steps: 7 },
                { id: 'programs', name: 'Programmes', desc: 'Upload and analyse construction programmes', icon: BarChart3, steps: 7 },
                { id: 'shop_drawings', name: 'Shop Drawings', desc: 'Track submissions through review cycles', icon: Package, steps: 7 },
                { id: 'drawings', name: 'Drawing Register', desc: 'Manage drawings and revision control', icon: Layers, steps: 7 },
                { id: 'reports', name: 'Reports', desc: 'Generate cross-project analytics', icon: TrendingUp, steps: 7, navigable: true },
                { id: 'executive_dashboard', name: 'Executive Dashboard', desc: 'Portfolio-level KPIs overview', icon: BarChart3, steps: 7, navigable: true },
                { id: 'tasks', name: 'Tasks', desc: 'Manage assignments and deadlines', icon: CheckCircle, steps: 7, navigable: true },
              ].map(tour => {
                const TourIcon = tour.icon;
                const status = walkthroughCtx?.statuses?.[tour.id];
                const isCompleted = status?.completed;
                return (
                  <button
                    key={tour.id}
                    onClick={() => launchTour(tour.id)}
                    className={`bg-white rounded-lg border p-4 hover:shadow-sm transition-all text-left group ${isCompleted ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 hover:border-cyan-300'}`}
                    data-testid={`tour-${tour.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isCompleted ? 'bg-emerald-100' : 'bg-cyan-50 group-hover:bg-cyan-100'} transition-colors`}>
                        <TourIcon className={`h-4 w-4 ${isCompleted ? 'text-emerald-600' : 'text-cyan-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-sm font-semibold text-slate-900">{tour.name}</h3>
                          {isCompleted && <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />}
                        </div>
                        <p className="text-[10px] text-slate-500 line-clamp-1">{tour.desc}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] text-slate-400">{tour.steps} steps</span>
                          <Play className="h-3 w-3 text-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          {tour.navigable && <Badge variant="outline" className="text-[8px] py-0 px-1">Click to start</Badge>}
                          {!tour.navigable && <Badge variant="outline" className="text-[8px] py-0 px-1">In-project</Badge>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Popular */}
          {popularArticles.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-slate-500" /> Popular Articles</h2>
              <div className="space-y-2">
                {popularArticles.map(a => (
                  <button key={a.id} onClick={() => goArticle(a.id)} className="w-full text-left bg-white rounded-lg border border-slate-200 p-3 hover:border-slate-300 transition-all flex items-center gap-3">
                    <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-slate-800">{a.title}</h3>
                    </div>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">{a.view_count || 0} views</span>
                    <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Still need help */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
            <MessageSquare className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <h3 className="text-sm font-bold text-slate-900 mb-1">Still need help?</h3>
            <p className="text-xs text-slate-500 mb-3">Can't find what you're looking for? Raise a support ticket.</p>
            <Button variant="outline" size="sm" onClick={() => navigate('/help')} className="gap-1 text-xs"><MessageSquare className="h-3 w-3" /> Go to Support Portal</Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
